import React from "react";
import { loadGoogleMaps } from "@lib/googleMapsLoader";

const AMMAN = { lat: 31.9539, lng: 35.9106 };
const r6 = (n) => Math.round(Number(n) * 1e6) / 1e6;

function pathToCoords(path) {
  const out = [];
  for (let i = 0; i < path.getLength(); i++) {
    const p = path.getAt(i);
    out.push({ lat: r6(p.lat()), lng: r6(p.lng()) });
  }
  return out;
}
function coordsEqual(a, b) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (r6(a[i].lat) !== r6(b[i].lat) || r6(a[i].lng) !== r6(b[i].lng)) return false;
  }
  return true;
}
function numericCoords(z) {
  return (z.coordinates || [])
    .filter((c) => c.lat !== "" && c.lng !== "" && Number.isFinite(Number(c.lat)) && Number.isFinite(Number(c.lng)))
    .map((c) => ({ lat: Number(c.lat), lng: Number(c.lng) }));
}

/**
 * Interactive editor: each zone is an editable + draggable Google Maps polygon.
 * - drag corners to reshape · drag midpoint handles to add points
 * - drag the area to move it · right-click a corner to delete it
 * - click an area to select it · draw a brand-new area
 * Two-way synced with the parent's `zones` state (map edits -> onZoneCoords;
 * external table/JSON edits -> polygon paths). A per-polygon `suppress` flag
 * prevents the two from fighting.
 */
export default function ZonesEditorMap({ zones, selected, onSelect, onZoneCoords, onDrawArea, orders = [], height = 460 }) {
  const ref = React.useRef(null);
  const mapRef = React.useRef(null);
  const polysRef = React.useRef([]); // [{ polygon, suppress }]
  const ordersRef = React.useRef([]); // order dot markers
  const drawRef = React.useRef({ pts: [], poly: null, markers: [], clickL: null, dblL: null });
  const freeRef = React.useRef({ down: null, move: null, up: null, docUp: null, poly: null, pressed: false, pts: [] });
  const cbRef = React.useRef({});
  cbRef.current = { onSelect, onZoneCoords, onDrawArea };

  const [ready, setReady] = React.useState(false);
  const [drawing, setDrawing] = React.useState(false);   // click-to-draw
  const [freehand, setFreehand] = React.useState(false); // drag-to-draw
  const [drawCount, setDrawCount] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    loadGoogleMaps().then(() => alive && setReady(true));
    return () => { alive = false; };
  }, []);

  // Clean up map overlays + any in-progress draw listeners on unmount.
  React.useEffect(() => {
    return () => {
      const g = window.google;
      const d = drawRef.current;
      if (g?.maps) {
        if (d.clickL) g.maps.event.removeListener(d.clickL);
        if (d.dblL) g.maps.event.removeListener(d.dblL);
      }
      if (d.poly) d.poly.setMap(null);
      d.markers.forEach((m) => m.setMap(null));
      for (const e of polysRef.current) e.polygon.setMap(null);
      ordersRef.current.forEach((m) => m.setMap(null));
      const f = freeRef.current;
      if (g?.maps) { for (const l of [f.down, f.move, f.up]) if (l) g.maps.event.removeListener(l); }
      if (f.docUp) document.removeEventListener("mouseup", f.docUp);
      if (f.poly) f.poly.setMap(null);
    };
  }, []);

  // Order dots overlay — light gray points so zones can be drawn around clusters.
  React.useEffect(() => {
    const g = window.google;
    const map = mapRef.current;
    if (!ready || !g?.maps || !map) return;
    for (const m of ordersRef.current) m.setMap(null);
    ordersRef.current = (orders || [])
      .filter((o) => Number.isFinite(Number(o.lat)) && Number.isFinite(Number(o.lng)))
      .map((o) => new g.maps.Marker({
        position: { lat: Number(o.lat), lng: Number(o.lng) },
        map, clickable: false, zIndex: 0,
        icon: { path: g.maps.SymbolPath.CIRCLE, scale: 3.4, fillColor: "#374151", fillOpacity: 0.85, strokeColor: "#fff", strokeWeight: 0.8 },
      }));
  }, [orders, ready]);

  // Create the map + drawing manager once.
  React.useEffect(() => {
    const g = window.google;
    if (!ready || !g?.maps || !ref.current || mapRef.current) return;

    mapRef.current = new g.maps.Map(ref.current, {
      center: AMMAN, zoom: 11, mapTypeControl: false, streetViewControl: false,
    });

    // Fit to all zones initially.
    const all = zones.flatMap(numericCoords);
    if (all.length) {
      const b = new g.maps.LatLngBounds();
      all.forEach((c) => b.extend(c));
      mapRef.current.fitBounds(b);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  // Reconcile polygons with the zones list.
  React.useEffect(() => {
    const g = window.google;
    const map = mapRef.current;
    if (!ready || !g?.maps || !map) return;

    const build = (i, z) => {
      const polygon = new g.maps.Polygon({
        paths: numericCoords(z),
        strokeColor: z.color, strokeWeight: 2, strokeOpacity: 0.9,
        fillColor: z.color, fillOpacity: 0.2,
        editable: true, draggable: true, zIndex: 1, map,
      });
      const entry = { polygon, suppress: false };
      const emit = () => {
        if (entry.suppress) return;
        cbRef.current.onZoneCoords?.(i, pathToCoords(polygon.getPath()));
      };
      const path = polygon.getPath();
      path.addListener("set_at", emit);
      path.addListener("insert_at", emit);
      path.addListener("remove_at", emit);
      polygon.addListener("dragend", emit);
      polygon.addListener("click", (e) => {
        // Easy add-point: click on an edge inserts a new corner right there.
        // (e.edge is the edge index when the click lands on the outline.)
        if (e.edge != null && e.vertex == null) {
          polygon.getPath().insertAt(e.edge + 1, e.latLng); // fires insert_at -> emit
        }
        cbRef.current.onSelect?.(i);
      });
      polygon.addListener("rightclick", (e) => {
        if (e.vertex != null && polygon.getPath().getLength() > 3) polygon.getPath().removeAt(e.vertex);
      });
      return entry;
    };

    // Rebuild all when the number of zones changes (add/remove).
    if (polysRef.current.length !== zones.length) {
      for (const e of polysRef.current) e.polygon.setMap(null);
      polysRef.current = zones.map((z, i) => build(i, z));
    }

    // Update style + (only-if-changed) path for each polygon.
    zones.forEach((z, i) => {
      const entry = polysRef.current[i];
      if (!entry) return;
      const poly = entry.polygon;
      poly.setOptions({
        strokeColor: z.color, fillColor: z.color,
        strokeWeight: selected === i ? 6 : 3, // thicker edge = easier click-to-add-point
        fillOpacity: selected === i ? 0.35 : 0.18,
        zIndex: selected === i ? 3 : 1,
      });
      const next = numericCoords(z);
      if (next.length >= 3 && !coordsEqual(pathToCoords(poly.getPath()), next)) {
        entry.suppress = true;
        poly.setPath(next);
        entry.suppress = false;
      }
    });
  }, [zones, selected, ready]);

  // While drawing, freeze existing zone polygons so a stray drag can't move
  // one (and it doesn't swallow the map's draw clicks). Restored on teardown.
  const setPolysInteractive = (on) => {
    for (const e of polysRef.current) e.polygon.setOptions({ editable: on, draggable: on, clickable: on });
  };

  // Custom click-to-draw (DrawingManager was removed from the Maps API in v3.65):
  // click points on the map to build a polygon, then Finish.
  const teardownDraw = () => {
    const g = window.google;
    const d = drawRef.current;
    if (d.clickL) g.maps.event.removeListener(d.clickL);
    if (d.dblL) g.maps.event.removeListener(d.dblL);
    if (d.poly) d.poly.setMap(null);
    d.markers.forEach((m) => m.setMap(null));
    if (mapRef.current) mapRef.current.setOptions({ disableDoubleClickZoom: false });
    setPolysInteractive(true);
    drawRef.current = { pts: [], poly: null, markers: [], clickL: null, dblL: null };
    setDrawCount(0);
  };

  const startDraw = () => {
    const g = window.google;
    const map = mapRef.current;
    if (!g?.maps || !map) return;
    setDrawing(true);
    setDrawCount(0);
    setPolysInteractive(false);
    map.setOptions({ disableDoubleClickZoom: true });
    const d = drawRef.current;
    d.pts = [];
    d.poly = new g.maps.Polygon({
      paths: [], strokeColor: "#9013FE", strokeWeight: 2, fillColor: "#9013FE",
      fillOpacity: 0.2, clickable: false, map, zIndex: 5,
    });
    d.clickL = map.addListener("click", (e) => {
      d.pts.push({ lat: e.latLng.lat(), lng: e.latLng.lng() });
      d.poly.setPath(d.pts);
      d.markers.push(new g.maps.Marker({
        position: e.latLng, map, clickable: false, zIndex: 6,
        icon: { path: g.maps.SymbolPath.CIRCLE, scale: 4, fillColor: "#9013FE", fillOpacity: 1, strokeColor: "#fff", strokeWeight: 1 },
      }));
      setDrawCount(d.pts.length);
    });
    d.dblL = map.addListener("dblclick", () => finishDraw());
  };

  const finishDraw = () => {
    const pts = drawRef.current.pts.slice();
    teardownDraw();
    setDrawing(false);
    if (pts.length >= 3) {
      cbRef.current.onDrawArea?.(pts.map((p) => ({ lat: r6(p.lat), lng: r6(p.lng) })));
    }
  };

  const cancelDraw = () => {
    teardownDraw();
    setDrawing(false);
  };

  // Freehand: press and drag to trace an area with the mouse (like a lasso).
  const teardownFreehand = () => {
    const g = window.google;
    const map = mapRef.current;
    const f = freeRef.current;
    if (g?.maps) { for (const l of [f.down, f.move, f.up]) if (l) g.maps.event.removeListener(l); }
    if (f.docUp) document.removeEventListener("mouseup", f.docUp);
    if (f.poly) f.poly.setMap(null);
    if (map) map.setOptions({ draggable: true, draggableCursor: null, disableDoubleClickZoom: false });
    setPolysInteractive(true);
    freeRef.current = { down: null, move: null, up: null, docUp: null, poly: null, pressed: false, pts: [] };
    setDrawCount(0);
  };

  const startFreehand = () => {
    const g = window.google;
    const map = mapRef.current;
    if (!g?.maps || !map) return;
    setFreehand(true);
    setDrawCount(0);
    setPolysInteractive(false);
    // Disable panning so the drag traces instead of moving the map.
    map.setOptions({ draggable: false, draggableCursor: "crosshair", disableDoubleClickZoom: true });
    const f = freeRef.current;
    f.pts = [];
    f.pressed = false;
    f.poly = new g.maps.Polygon({
      paths: [], strokeColor: "#9013FE", strokeWeight: 2, fillColor: "#9013FE",
      fillOpacity: 0.2, clickable: false, map, zIndex: 5,
    });
    const MIN_DEG = 0.00025; // ~25m: throttle captured points
    const pushPoint = (latLng) => {
      const p = { lat: latLng.lat(), lng: latLng.lng() };
      const last = f.pts[f.pts.length - 1];
      if (last && Math.abs(last.lat - p.lat) < MIN_DEG && Math.abs(last.lng - p.lng) < MIN_DEG) return;
      f.pts.push(p);
      f.poly.setPath(f.pts);
      setDrawCount(f.pts.length);
    };
    f.down = map.addListener("mousedown", (e) => {
      f.pressed = true; f.pts = []; f.poly.setPath([]); pushPoint(e.latLng);
    });
    f.move = map.addListener("mousemove", (e) => { if (f.pressed) pushPoint(e.latLng); });
    f.up = map.addListener("mouseup", () => finishFreehand());
    // In case the mouse is released off the map.
    f.docUp = () => { if (f.pressed) finishFreehand(); };
    document.addEventListener("mouseup", f.docUp);
  };

  const finishFreehand = () => {
    const pts = freeRef.current.pts.slice();
    teardownFreehand();
    setFreehand(false);
    if (pts.length >= 3) {
      cbRef.current.onDrawArea?.(pts.map((p) => ({ lat: r6(p.lat), lng: r6(p.lng) })));
    }
  };

  const cancelFreehand = () => {
    teardownFreehand();
    setFreehand(false);
  };

  return (
    <div className="mz-editor">
      <div className="mz-map-tools">
        {!drawing && !freehand ? (
          <>
            <button className="oc-btn" onClick={startDraw} type="button">✏️ Draw new area</button>
            <button className="oc-btn" onClick={startFreehand} type="button">🖊️ Freehand draw</button>
            <span className="mz-muted">
              <b>Click an edge to add a point</b> · drag corners to reshape · drag the area to move it ·
              right-click a corner to delete · click an area to select.
            </span>
          </>
        ) : drawing ? (
          <>
            <button className="oc-btn primary" onClick={finishDraw} type="button" disabled={drawCount < 3}>
              ✓ Finish area{drawCount ? ` (${drawCount} pts)` : ""}
            </button>
            <button className="oc-btn" onClick={cancelDraw} type="button">Cancel</button>
            <span className="mz-muted">Click points on the map to outline the area (at least 3), then Finish — or double-click to finish.</span>
          </>
        ) : (
          <>
            <button className="oc-btn primary" onClick={finishFreehand} type="button" disabled={drawCount < 3}>
              ✓ Use shape{drawCount ? ` (${drawCount} pts)` : ""}
            </button>
            <button className="oc-btn" onClick={cancelFreehand} type="button">Cancel</button>
            <span className="mz-muted"><b>Press and drag on the map</b> to trace the area with your mouse, then release to finish.</span>
          </>
        )}
      </div>
      {!ready && (
        <div style={{ height, display: "grid", placeItems: "center", background: "#f5f5f5", borderRadius: 8 }}>Loading map…</div>
      )}
      <div ref={ref} style={{ width: "100%", height, display: ready ? "block" : "none", borderRadius: 8 }} />
    </div>
  );
}
